import type { Phase } from '@ics-portal/tra';
import { type PhaseName } from '@ics-portal/tra/src/v1/phases';
import useTRA from './use-tra';
import useTRAPhase from './use-tra-phase';

interface UsePhaseComplete {
    isComplete: (phase: Phase) => boolean;
    isRootCompleteByName: (phaseName: PhaseName) => boolean;
}

export function usePhaseComplete(): UsePhaseComplete {
    const { data } = useTRA();

    function isComplete(phase: Phase): boolean {
        const phaseToValidate = getPhaseToValidate(phase);
        if (!phaseToValidate || !data) {
            return false;
        }

        const phasesList = data.phases
            .getAll()
            .filter(
                (p) =>
                    p.rootPhaseId &&
                    parseInt(p.id) <= parseInt(phaseToValidate.id),
            );

        return phasesList.every(
            (p) => !data.phaseValidator.getCompleteErrors(p),
        );
    }

    function isRootCompleteByName(phaseName: PhaseName): boolean {
        const { rootPhases, currentRootPhase } = useTRAPhase();
        const foundRootPhase = rootPhases.find(
            (phase) => phase.name === phaseName,
        );

        if (foundRootPhase && currentRootPhase?.isAfter(foundRootPhase)) {
            return true;
        }

        return false;
    }

    function getPhaseToValidate(phase: Phase): Phase | undefined {
        if (phase.rootPhaseId) {
            return phase;
        }
        const childPhases = data?.phases.getChildPhases(phase);
        return childPhases ? childPhases[childPhases.length - 1] : undefined;
    }

    return { isComplete, isRootCompleteByName };
}
